/**
 * Audio settings store — user-controllable audio toggles the Sound tab writes
 * and every audio consumer reads.
 *
 * Three orthogonal channels:
 * - music: background ambient loops (WorldAudioController.playAmbient)
 * - sfx:   proximity + UI + covered-building cues (playProximity/playUi +
 *          playCoveredBuildingSound + client-side PresenceSoundController).
 *          NOTE: does NOT gate the agent-side thinking / filler / heard cues —
 *          those are pinned on as part of the creative conversation feedback
 *          loop (they're gated only by the agent's ENABLE_* env flags).
 * - voice: the world-nav LiveKit agent (useNavigationVoice)
 *
 * Two additional env-derived gates layer on top so a build can hard-disable a
 * channel without touching the user preference:
 *
 *   VITE_WORLD_AUDIO_SFX_ENABLED  (default true)  → music + sfx
 *   VITE_NAV_VOICE_ENABLED        (default false) → voice
 *
 * Effective(channel) = envGate(channel) AND userPref(channel).
 *
 * Persistence: mirrors to localStorage under `wbm_sound_*`. Older builds wrote
 * the same keys; existing values are picked up on first read.
 */
import { create } from "zustand";

const STORAGE_KEYS = {
  music: "wbm_sound_music",
  sfx: "wbm_sound_sfx",
  voice: "wbm_sound_voice",
} as const;

function readBool(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === "1";
  } catch {
    return fallback;
  }
}

function writeBool(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? "1" : "0");
  } catch {
    /* ignore */
  }
}

/** Build-time gate for the ambient + SFX pipeline. Default on. */
export const WORLD_AUDIO_SFX_ENV_ENABLED =
  (import.meta.env.VITE_WORLD_AUDIO_SFX_ENABLED ?? "true") !== "false";

/** Build-time gate for the nav-voice LiveKit agent. Default OFF. */
export const NAV_VOICE_ENV_ENABLED =
  import.meta.env.VITE_NAV_VOICE_ENABLED === "true";

export interface AudioSettingsState {
  /** User's Background music preference (on/off). */
  music: boolean;
  /** User's Sound effects preference (proximity, UI, covered chimes, presence). */
  sfx: boolean;
  /** User's World-narration voice preference (LiveKit nav agent). */
  voice: boolean;
  setMusic: (v: boolean) => void;
  setSfx: (v: boolean) => void;
  setVoice: (v: boolean) => void;
}

export const useAudioSettings = create<AudioSettingsState>()((set) => ({
  music: readBool(STORAGE_KEYS.music, true),
  sfx: readBool(STORAGE_KEYS.sfx, true),
  // Voice defaults OFF until the operator turns it on. The nav agent is a paid
  // LiveKit session per kid, so a background-on default would burn quota.
  voice: readBool(STORAGE_KEYS.voice, false),
  setMusic: (v) => {
    writeBool(STORAGE_KEYS.music, v);
    set({ music: v });
  },
  setSfx: (v) => {
    writeBool(STORAGE_KEYS.sfx, v);
    set({ sfx: v });
  },
  setVoice: (v) => {
    writeBool(STORAGE_KEYS.voice, v);
    set({ voice: v });
  },
}));

/** Effective SFX-channel gate (env AND user). Consumers use this, not `sfx` alone. */
export function sfxEnabled(state: AudioSettingsState = useAudioSettings.getState()): boolean {
  return WORLD_AUDIO_SFX_ENV_ENABLED && state.sfx;
}

/** Effective music-channel gate (env AND user). Music rides the SFX env flag. */
export function musicEnabled(state: AudioSettingsState = useAudioSettings.getState()): boolean {
  return WORLD_AUDIO_SFX_ENV_ENABLED && state.music;
}

/** Effective nav-voice-channel gate (env AND user). */
export function voiceEnabled(state: AudioSettingsState = useAudioSettings.getState()): boolean {
  return NAV_VOICE_ENV_ENABLED && state.voice;
}
